import React, { useContext, useState } from 'react';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import ControlSystemKeyModal from '@totem/components/controlSystemDetail/keys/ControlSystemKeyModal';
import ControlSystemKeysContext from '@totem/components/controlSystemDetail/keys/ControlSystemKeysContext';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ForeignKey } from '@totem/types/common';
import { formatDate, isNotNull } from '@totem/utilities/common';
import { CONTROL_SYSTEMS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const ControlSystemKeysPanel = () => {
  const { getApiToken } = useContext(UserProfileContext);
  const { data, loading, controlSystemId, requestRefresh } = useContext(
    ControlSystemKeysContext,
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [editKey, setEditKey] = useState<ForeignKey>(null);

  const handleDelete = (record: ForeignKey) => {
    fetch(`${CONTROL_SYSTEMS_ENDPOINT}/${controlSystemId}/keys`, {
      method: 'DELETE',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify({
        system: record.system,
        instance: record.instance,
        key: record.key,
      }),
    }).then((res) => {
      CheckResponseShowError(res);

      requestRefresh();
    });
  };

  const columns: ColumnProps<ForeignKey>[] = [
    {
      title: 'System',
      dataIndex: 'system',
      key: 'system',
    },
    {
      title: 'Instance',
      dataIndex: 'instance',
      key: 'instance',
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
    },
    {
      title: 'UID',
      dataIndex: 'uid',
      key: 'uid',
      render: (_, record) => record.uid || '—',
    },
    {
      title: 'Created By',
      dataIndex: 'createdByName',
      key: 'createdByName',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_, record) => formatDate(record.createdAt),
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditKey(record);

              setModalOpen(true);
            }}
          />
          <Popconfirm
            title="Delete this key?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <IBCard
        isCovered
        title="Keys"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditKey(null);

              setModalOpen(true);
            }}
          >
            Add Key
          </Button>
        }
      >
        <IBTable
          columns={columns}
          dataSource={isNotNull(data) ? data : []}
          loading={loading}
          rowKey={(record) =>
            `${record.system}-${record.instance}-${record.key}`
          }
          pagination={{ showSizeChanger: true }}
        />
      </IBCard>

      <ControlSystemKeyModal
        open={modalOpen}
        keyRecord={editKey}
        onClose={() => {
          setModalOpen(false);

          setEditKey(null);
        }}
      />
    </>
  );
};

export default ControlSystemKeysPanel;
